import React, { useState, useMemo, useEffect } from 'react';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import CPPUtils from 'services/CPPUtils';
import CalendarUtils from 'services/CalendarUtils';
import { useAppSelector } from 'careand-redux/store';
import { calculateCrCl, UMOL_PER_L_TO_MG_PER_DL } from './calculateCrCl';

const DEFAULT_ARRAY = [];

function roundDisplay(n, digits = 2) {
  if (!isFinite(n)) return '';
  return parseFloat(n.toFixed(digits)).toString();
}

// Cockcroft-Gault: CrCl (mL/min) = ((140 - age) * weightKg) / (72 * Scr_mg/dL), * 0.85 if Female.
// This is an estimate, not a measured GFR. Prefer CKD-EPI eGFR when a true GFR is needed.
const CrClCalculatorModal = ({ patient, onSubmit }) => {
  const patientHr = useAppSelector((state) => state.healthRecords.byId[patient?.id]);
  const measurements = CPPUtils.getPatientMeasurements(patientHr);
  const patientSummary =
    useAppSelector((state) => state.healthRecords.byId[patient?.id]?.dataSummary) || DEFAULT_ARRAY;

  // Care&'s Dynacare-fed CREATININE values are in µmol/L (Canadian convention).
  // Held in canonical µmol/L; converted on display when the user toggles units.
  const defaultCreatinineUmol = useMemo(() => {
    return patientSummary.find(
      ({ metric, origin }) => metric.id === 'CREATININE' && origin === 'Lab',
    )?.last_value?.value1;
  }, [patientSummary]);

  const initialGender =
    patient?.gender === 'Male' || patient?.gender === 'Female' ? patient.gender : '';

  const [age, setAge] = useState(CalendarUtils.calculateAgeNumber(patient?.date_of_birth) || '');
  const [gender, setGender] = useState(initialGender);
  const [weight, setWeight] = useState('');
  const [unit, setUnit] = useState('umol/L');
  const [creatinine, setCreatinine] = useState('');

  useEffect(() => {
    if (measurements.weight) setWeight('' + measurements.weight);
  }, [measurements.weight]);

  // Prefill creatinine in the currently-selected display unit.
  useEffect(() => {
    if (defaultCreatinineUmol == null) return;
    const v =
      unit === 'mg/dL'
        ? Number(defaultCreatinineUmol) / UMOL_PER_L_TO_MG_PER_DL
        : Number(defaultCreatinineUmol);
    setCreatinine(roundDisplay(v, unit === 'mg/dL' ? 2 : 0));
    // Only re-runs when the source lab value changes; unit changes handled by
    // handleUnitChange so user edits aren't clobbered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCreatinineUmol]);

  // Convert displayed value when the user toggles the unit, so the number and
  // label always agree. Without this, a 85 µmol/L value re-read as 85 mg/dL
  // produces a CrCl ~88x too low.
  function handleUnitChange(nextUnit) {
    if (nextUnit === unit) return;
    const cur = parseFloat(creatinine);
    if (isFinite(cur) && cur > 0) {
      const converted =
        nextUnit === 'mg/dL' ? cur / UMOL_PER_L_TO_MG_PER_DL : cur * UMOL_PER_L_TO_MG_PER_DL;
      setCreatinine(roundDisplay(converted, nextUnit === 'mg/dL' ? 2 : 0));
    }
    setUnit(nextUnit);
  }

  const result = useMemo(() => {
    const crcl = calculateCrCl({
      ageYears: age,
      weightKg: weight,
      serumCreatinine: creatinine,
      scrUnit: unit,
      gender,
    });
    return crcl == null ? null : crcl.toFixed(1);
  }, [age, weight, creatinine, unit, gender]);

  const resultText = result ? `${result} mL/min` : 'N/A';

  function getResultText() {
    return (
      `Creatinine Clearance (Cockcroft-Gault, estimate): ${resultText}\n` +
      `Age: ${age}\nGender: ${gender}\nWeight: ${weight} kg\n` +
      `Serum Creatinine: ${creatinine} ${unit}`
    );
  }

  async function copyToClipboard() {
    await navigator.clipboard.writeText(getResultText());
  }

  return (
    <div className="modal">
      <div className="title">Creatinine Clearance (Cockcroft-Gault)</div>
      <div className="padded-box">
        <div className="row">
          <DefaultTextField
            name="age"
            label="Age (years)"
            value={age}
            onChange={(event) => setAge(event.target.value)}
            style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
          />
          <DefaultSelectField
            label="Gender"
            name="gender"
            value={gender}
            onChange={(event) => setGender(event.target.value)}
            inputs={['', 'Male', 'Female']}
            style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
          />
          <DefaultTextField
            name="weight"
            label="Weight (kg)"
            value={weight}
            onChange={(event) => setWeight(event.target.value)}
            style={{ width: 'calc(100% - 15px)', marginRight: '15px' }}
          />
          <DefaultTextField
            name="creatinine"
            label={`Serum Creatinine (${unit})`}
            value={creatinine}
            onChange={(event) => setCreatinine(event.target.value)}
            style={{ width: 'calc(60% - 15px)', marginRight: '15px' }}
          />
          <DefaultSelectField
            label="Unit"
            name="unit"
            value={unit}
            onChange={(event) => handleUnitChange(event.target.value)}
            inputs={['umol/L', 'mg/dL']}
            style={{ width: 'calc(40% - 15px)', marginRight: '15px' }}
          />
        </div>
        <div className="row" style={{ marginTop: '10px' }}>
          <DefaultTextField
            name="result"
            label="CrCl"
            value={resultText}
            disabled={true}
            style={{ width: 'calc(100% - 15px)', marginRight: '15px' }}
          />
        </div>
        <div className="row" style={{ marginTop: '6px', fontSize: '12px', opacity: 0.7 }}>
          Cockcroft-Gault is an estimate of CrCl based on actual body weight; it is not a measured
          GFR. For chronic-kidney-disease staging, use a CKD-EPI eGFR.
        </div>
        <DefaultButton
          name={onSubmit ? 'Submit' : 'Copy to Clipboard'}
          icon={onSubmit ? undefined : 'clipboard'}
          onClick={onSubmit ? () => onSubmit(getResultText()) : copyToClipboard}
          style={{ width: '100%', marginTop: '10px' }}
          disabled={!result}
          className="blue"
        />
      </div>
    </div>
  );
};

export default CrClCalculatorModal;
